import axios from "axios";

import constants from "../../constants";
import { isApiError } from "../api/errors";

/**
 * Domains sold at Keepo (registered through Namecheap), backed by the API's `/domains` endpoints. Prices are
 * in BRL with Keepo's margin: 1, 2 or 5 years (the longer cycles with a discount), a one-time purchase.
 */

/** The cheapest a year can cost (the API's floor, DomainPricing::DEFAULT_MIN_PRICE): what the "from" prices say. */
export const FROM_PRICE = 29.9;

/** A cycle sold: how many years, its price (with the cycle's discount) and the full one (struck through). */
export type DomainCycle = { years: number; price: number; fullPrice: number };

export type DomainPrice = {
  tld: string;
  /** The year sold, in BRL. */
  register: number;
  /** One more year, at today's cost, in BRL: shown at purchase (often far above a promotional first year). */
  renew: number;
  /** 1, 2 and 5 years. */
  cycles?: DomainCycle[];
  /** The market price of a year in BRL (Namecheap's retail with the IOF), when known: for the "X% OFF" tag. */
  retail?: number | null;
};

/** The cycles of a price (just the year when the API doesn't send them). */
export const cyclesOf = (price: DomainPrice): DomainCycle[] =>
  price.cycles?.length ? price.cycles : [{ years: 1, price: price.register, fullPrice: price.register }];

export type DomainResult = {
  domain: string;
  tld: string;
  available: boolean;
  /** Premium names (a much higher price set by the registry): not sold at Keepo. */
  premium?: boolean;
  /** An extension Keepo doesn't sell (e.g. .com.br). */
  notSold?: boolean;
  price?: DomainPrice | null;
};

export type DomainSearchResult = { exact: DomainResult; suggestions: DomainResult[] };

export type DomainStatus = "active" | "expiring" | "expired" | "pending";

export type OwnedDomain = {
  domain: string;
  status: DomainStatus;
  /** Bought by Pix/boleto, not paid yet (status pending). */
  paymentPending?: boolean;
  /** YYYY-MM-DD; none until it's registered. */
  expiresAt: string | null;
  /** The Keepo it points to, if any. */
  /** The Keepo it opens; `icon`: its favicon (SEO), else its logo, from our bucket (null: none). */
  keepo?: { siteId: string; title: string; url: string; icon?: string | null } | null;
  privacy: boolean;
  locked: boolean;
  /** What a renewal cost at purchase, in BRL. */
  renewPrice?: number | null;
};

/** Error codes of the search. */
export const DOMAIN_SEARCH_ERRORS = {
  invalid: "INVALID_DOMAIN",
  rateLimit: "DOMAIN_SEARCH_RATE_LIMIT",
  unavailable: "DOMAIN_SEARCH_UNAVAILABLE",
} as const;

/** The name asked for and the suggested extensions, with prices. */
export const searchDomains = async (query: string): Promise<DomainSearchResult> => {
  const response = await axios.get(`${constants.api}/domains/search`, { params: { q: query } });
  return response.data;
};

/** How many extensions are on sale (the search shows a few of them, then "+N"). */
export const getDomainExtensionsTotal = async (): Promise<number> => {
  const response = await axios.get(`${constants.api}/domains/extensions`);
  return response.data.total;
};

/** The person's domains (bought, being registered or waiting for the payment). */
export const getMyDomains = async (): Promise<OwnedDomain[]> => {
  const response = await axios.get(`${constants.api}/domains`);
  return response.data;
};

/**
 * Right after the card payment went through: the API checks it with Stripe and starts the registration
 * without waiting for the webhook (whichever comes first), so My domains already lists it as being registered.
 */
export const confirmDomainPayment = async (domain: string): Promise<OwnedDomain> => {
  const response = await axios.post(`${constants.api}/domains/${encodeURIComponent(domain)}/confirm`);
  return response.data;
};

/** The domain's owner (registrant): the billing details plus e-mail and phone, which registering asks for. */
export type DomainOwner = {
  name: string;
  email: string;
  phone: string;
  country: string;
  postalCode: string;
  line1: string;
  number: string;
  line2?: string;
  district?: string;
  city: string;
  state: string;
  /** CPF/CNPJ (Pix). */
  document?: string;
};

export type DomainOrder = {
  domainId: string;
  domain: string;
  method: "card" | "pix";
  paymentIntentId: string;
  /** In cents. */
  amount: number;
  /** Card (and wallets): confirmed by Stripe.js. */
  clientSecret?: string;
  /** Pix: the code to pay (it expires in 30 minutes). */
  pix?: { code: string; qrImageUrl: string | null; expiresAt: string | null };
};

/** Error codes of buying a domain. */
export const DOMAIN_ORDER_ERRORS = {
  taken: "DOMAIN_TAKEN",
  notSold: "DOMAIN_NOT_SOLD",
  plusRequired: "PLUS_REQUIRED",
  /** Sales paused (Namecheap's balance can't pay for it): Keepo's team is alerted. */
  unavailable: "DOMAIN_SALES_UNAVAILABLE",
} as const;

/** Buys a domain (1, 2 or 5 years, once): the payment to confirm (card) or to pay (Pix). */
export const createDomainOrder = async (order: {
  domain: string;
  /** 1, 2 or 5. */
  years: number;
  method: "card" | "pix";
  siteId?: string | null;
  owner: DomainOwner;
}): Promise<DomainOrder> => {
  const response = await axios.post(`${constants.api}/domains`, order);
  return response.data;
};

export type DnsRecordType = "A" | "AAAA" | "CNAME" | "MX" | "TXT" | "URL";

export const DNS_TYPES: DnsRecordType[] = ["A", "AAAA", "CNAME", "MX", "TXT", "URL"];

export type DnsRecord = {
  id: string;
  type: DnsRecordType;
  /** "@" for the domain itself, or the subdomain ("www", "mail"…). */
  host: string;
  value: string;
  /** MX only: lower goes first. */
  priority?: number | null;
  /** In seconds; "auto" lets Namecheap pick (30 min). */
  ttl: "auto" | number;
  /** Keepo's (it points the domain to the connected Keepo): locked. */
  managed?: boolean;
};

export type DomainDns = {
  /** keepo: Keepo's DNS (Namecheap's), records editable; custom: nameservers of their own. */
  mode: "keepo" | "custom";
  nameservers: string[];
  records: DnsRecord[];
};

export type EmailForward = { alias: string; to: string };

/** Error codes of a domain's settings. */
export const DOMAIN_SETTINGS_ERRORS = {
  invalid: "INVALID_DOMAIN_SETTINGS",
  provider: "DOMAIN_PROVIDER_ERROR",
  notFound: "DOMAIN_NOT_FOUND",
} as const;

/** Which message a refused connection of a domain to a Keepo needs (a key of the keepo errors, `generic` otherwise). */
export const keepoConnectErrorKey = (
  error: unknown
): "plusRequired" | "keepoHasDomain" | "removalInProgress" | "generic" =>
  isApiError(error, "PLUS_REQUIRED")
    ? "plusRequired"
    : isApiError(error, "KEEPO_HAS_DOMAIN")
      ? "keepoHasDomain"
      : isApiError(error, "DOMAIN_REMOVAL_IN_PROGRESS")
        ? "removalInProgress"
        : "generic";

/** One of the person's domains, with its owner. */
export const getDomain = async (domain: string): Promise<OwnedDomain & { owner?: Partial<DomainOwner> }> => {
  const response = await axios.get(`${constants.api}/domains/${encodeURIComponent(domain)}`);
  return response.data;
};

export const getDomainDns = async (domain: string): Promise<DomainDns> => {
  const response = await axios.get(`${constants.api}/domains/${encodeURIComponent(domain)}/dns`);
  return response.data;
};

/** The person's records, all of them (Keepo's are kept by the API). */
export const saveDomainDns = async (domain: string, records: DnsRecord[]): Promise<DomainDns> => {
  const response = await axios.put(`${constants.api}/domains/${encodeURIComponent(domain)}/dns`, {
    records: records.filter(record => !record.managed),
  });
  return response.data;
};

/** Nameservers of their own, or [] to go back to Keepo's DNS. */
export const setDomainNameservers = async (domain: string, nameservers: string[]): Promise<DomainDns> => {
  const response = await axios.put(`${constants.api}/domains/${encodeURIComponent(domain)}/nameservers`, {
    nameservers,
  });
  return response.data;
};

export const getEmailForwarding = async (domain: string): Promise<EmailForward[]> => {
  const response = await axios.get(`${constants.api}/domains/${encodeURIComponent(domain)}/email-forwarding`);
  return response.data;
};

export const saveEmailForwarding = async (domain: string, forwards: EmailForward[]): Promise<EmailForward[]> => {
  const response = await axios.put(`${constants.api}/domains/${encodeURIComponent(domain)}/email-forwarding`, {
    forwards,
  });
  return response.data;
};

/** The transfer lock and/or WHOIS privacy. */
export const updateDomainSecurity = async (
  domain: string,
  changes: { locked?: boolean; privacy?: boolean }
): Promise<OwnedDomain> => {
  const response = await axios.put(`${constants.api}/domains/${encodeURIComponent(domain)}/security`, changes);
  return response.data;
};

export const updateDomainOwner = async (
  domain: string,
  owner: DomainOwner
): Promise<OwnedDomain & { owner?: Partial<DomainOwner> }> => {
  const response = await axios.put(`${constants.api}/domains/${encodeURIComponent(domain)}/owner`, { owner });
  return response.data;
};

/** Points the domain to one of the person's Keepos (PLUS); errors PLUS_REQUIRED, KEEPO_HAS_DOMAIN. */
export const connectDomainKeepo = async (domain: string, siteId: string): Promise<OwnedDomain> => {
  const response = await axios.put(`${constants.api}/domains/${encodeURIComponent(domain)}/keepo`, { siteId });
  return response.data;
};

/** The domain stops pointing to its Keepo (it stays the person's). */
export const disconnectDomainKeepo = async (domain: string): Promise<OwnedDomain> => {
  const response = await axios.delete(`${constants.api}/domains/${encodeURIComponent(domain)}/keepo`);
  return response.data;
};
